import type { QuotationResponse } from '@palaccount/contracts';
import { ArrowRightLeft, Check, Copy, Pencil, Send, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Amount } from '@/components/amount';
import {
  BulkExportButton,
  BulkPdfButton,
  DocumentListPage,
  DocumentPdfButton,
  documentLocale,
  normalisePaged,
  type DocumentListConfig,
} from '@/components/document-list';
import { Gated } from '@/components/permission-gate';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { daysBetween } from '@/lib/dates';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerCause } from '@/lib/server-messages';
import { useCompanyToday } from '@/lib/session';

import { allowedTransitions, canConvert, canRevise } from './quote-flow';
import {
  fetchQuotationsPage,
  useConvertToInvoice,
  useConvertToOrder,
  useNewRevision,
  useQuotation,
  useQuotations,
  useQuotationStats,
  useSetQuotationStatus,
} from './quotes-api';
import { quotationSteps } from './sales-steppers';
import { SalesTabs } from './sales-tabs';

const STATUSES: QuotationResponse['status'][] = ['draft', 'sent', 'accepted', 'rejected', 'expired', 'converted'];
/**
 * SAL-01 §7's six chips through the one palette: Draft grey, Sent blue
 * (active), Accepted green, Rejected red, Expired amber (DECISION 9 —
 * waiting, not fatal), Converted green-faded (finished and filed).
 */
const TONES = {
  draft: 'draft',
  sent: 'posted',
  accepted: 'completed',
  rejected: 'cancelled',
  expired: 'overdue',
  converted: 'closed',
} as const;

type QuotationStats = ReturnType<typeof useQuotationStats>['data'] & object;

/**
 * SAL-01's list on the shared list-and-detail page (wave 3B). Reading it
 * runs the server's expiry sweep, so a quotation past its validity arrives
 * Expired (BR-SAL-01-09). The tiles are the register's; the selected
 * quotation shows the chain it may still become — the conversions are the
 * stepper's acts while R4 allows them — its revision and days of validity,
 * and the W1 transitions the editor offers (BR-SAL-01-02), the same server
 * refusing the same bad jumps.
 */
export function QuotesListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const today = useCompanyToday();
  const locale = documentLocale(i18n.language);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const selectedId = params.get('selected') ?? '';
  const setStatus = useSetQuotationStatus(selectedId);
  const newRevision = useNewRevision(selectedId);
  const convertToInvoice = useConvertToInvoice(selectedId);
  const convertToOrder = useConvertToOrder(selectedId);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.edit');
      setError(localizeServerCause(t, thrown, 'doclist.actFailed'));
    } finally {
      setBusy(false);
    }
  };
  const convertToOrderNow = () =>
    void act(async () => {
      const result = await convertToOrder.mutateAsync();
      void navigate(`/sales/orders/${result.orderId}/edit`);
    });
  const convertToInvoiceNow = () =>
    void act(async () => {
      const result = await convertToInvoice.mutateAsync();
      void navigate(`/sales/${result.invoiceId}`);
    });

  const statusLabel = (status: QuotationResponse['status']) => t(`quotes.status.${status}`);
  const chip = (quote: QuotationResponse) => <StatusChip status={quote.status} tones={TONES} ns="quotes" />;
  const exportColumns = [
    { header: t('quotes.colNo'), value: (q: QuotationResponse) => q.code },
    { header: t('common.date'), value: (q: QuotationResponse) => q.quotationDate, kind: 'date' as const },
    { header: t('quotes.colValidUntil'), value: (q: QuotationResponse) => q.validUntil, kind: 'date' as const },
    { header: t('sales.colCustomer'), value: (q: QuotationResponse) => q.partnerName },
    { header: t('sales.colStatus'), value: (q: QuotationResponse) => statusLabel(q.status) },
    { header: t('sales.exportColCurrency'), value: (q: QuotationResponse) => q.currency },
    { header: t('sales.colGrandTotal'), value: (q: QuotationResponse) => q.grandTotal, kind: 'number' as const },
  ];
  const pdfRequest = (quote: QuotationResponse) => ({ kind: 'quotation' as const, locale, subjectId: quote.id });
  const toFilters = (filters: { status: QuotationResponse['status'] | ''; q: string; dateFrom: string; dateTo: string; partnerId: string }) => ({
    status: filters.status || undefined,
    q: filters.q || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    partnerId: filters.partnerId || undefined,
  });

  const config: DocumentListConfig<QuotationResponse, QuotationResponse['status'], QuotationStats> = {
    titleKey: 'quotes.title',
    tabs: <SalesTabs />,
    newAction: { to: '/sales/quotes/new', labelKey: 'common.new', permission: 'sales.create', reasonKey: 'perm.cannot.createQuotes' },
    statuses: STATUSES,
    statusLabel,
    search: { labelKey: 'quotes.searchLabel', placeholderKey: 'quotes.searchPlaceholder' },
    dateRange: true,
    partner: { kind: 'customer' },
    useList: (filters, page, pageSize) => {
      const query = useQuotations(toFilters(filters), page, pageSize);
      return { data: normalisePaged(query.data, 'quotations'), isPending: query.isPending, isError: query.isError };
    },
    useById: (id) => useQuotation(id ?? undefined),
    kpis: {
      useStats: () => {
        const stats = useQuotationStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      tiles: [
        { key: 'open', labelKey: 'quotes.tileOpen', format: 'count', value: (s) => s.openCount },
        { key: 'openValue', labelKey: 'quotes.tileOpenValue', format: 'money', value: (s) => s.openValue, currency: (s) => s.currency },
        { key: 'winRate', labelKey: 'quotes.tileWinRate', format: 'pct', value: (s) => s.winRatePct, tone: 'pos' },
        { key: 'expiring', labelKey: 'quotes.tileExpiring', format: 'count', value: (s) => s.expiring7dCount, tone: 'warn' },
      ],
    },
    card: (quote) => ({
      code: quote.code,
      chip: chip(quote),
      title: quote.partnerName,
      meta: (
        <>
          <bdi dir="ltr">{quote.quotationDate}</bdi>
          {quote.revisionNo > 1 ? <bdi dir="ltr" className="ms-2 font-mono">{`v${quote.revisionNo}`}</bdi> : null}
        </>
      ),
      amount: { value: quote.grandTotal, currency: quote.currency },
    }),
    columns: { titleKey: 'sales.colCustomer', metaKey: 'common.date', amountKey: 'sales.colGrandTotal', statusKey: 'sales.colStatus' },
    emptyKey: 'quotes.empty',
    selectOneKey: 'quotes.selectOne',
    notice: error ? (
      <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    ) : null,
    export: {
      filename: 'quotations',
      columns: exportColumns,
      fetchPage: (filters, page, pageSize) => fetchQuotationsPage(toFilters(filters), page, pageSize),
    },
    bulkActions: (rows) => (
      <>
        <BulkPdfButton requests={rows.map(pdfRequest)} />
        <BulkExportButton filename="quotations" columns={exportColumns} rows={rows} />
      </>
    ),
    detail: {
      chip,
      /* Wave 1B's linked-document chips: the order or invoice it became, each OPENING it. */
      links: (quote) => [
        ...(quote.convertedOrderCode
          ? [
              {
                key: 'order',
                to: `/sales/orders?selected=${quote.convertedOrderId}`,
                tone: 'blue' as const,
                label: <bdi dir="ltr">{quote.convertedOrderCode}</bdi>,
              },
            ]
          : []),
        ...(quote.convertedInvoiceCode
          ? [
              {
                key: 'invoice',
                to: `/sales/${quote.convertedInvoiceId}`,
                tone: 'blue' as const,
                label: <bdi dir="ltr">{quote.convertedInvoiceCode}</bdi>,
              },
            ]
          : []),
      ],
      subline: (quote) => (
        <>
          {quote.partnerName} · {t('quotes.colValidUntil')} <bdi dir="ltr">{quote.validUntil}</bdi>
        </>
      ),
      steps: {
        labelKey: 'orders.stepper',
        of: (quote) => quotationSteps(quote, { convertToOrder: convertToOrderNow, convertToInvoice: convertToInvoiceNow, busy }),
      },
      /* "v{n} · {days} days left" — the validity counted from the company's today; past it, the window has ended. */
      progress: (quote) => {
        const left = daysBetween(today, quote.validUntil);
        return left < 0
          ? t('quotes.listProgressExpired', { rev: quote.revisionNo })
          : t('quotes.listProgress', { rev: quote.revisionNo, days: left });
      },
      actions: (quote) => {
        const transitions = allowedTransitions(quote.status);
        return (
          <>
            <Button asChild variant="outline">
              <Link to={`/sales/quotes/${quote.id}`}>
                <Pencil aria-hidden /> {quote.status === 'draft' ? t('common.edit') : t('doclist.open')}
              </Link>
            </Button>
            {transitions.includes('sent') ? (
              <Gated permission="sales.edit" reasonKey="perm.cannot.moveQuotes">
                <Button onClick={() => void act(() => setStatus.mutateAsync({ status: 'sent' }))} disabled={busy}>
                  <Send aria-hidden /> {t('quotes.actSend')}
                </Button>
              </Gated>
            ) : null}
            {transitions.includes('accepted') ? (
              <Gated permission="sales.edit" reasonKey="perm.cannot.moveQuotes">
                <Button onClick={() => void act(() => setStatus.mutateAsync({ status: 'accepted' }))} disabled={busy}>
                  <Check aria-hidden /> {t('quotes.actAccept')}
                </Button>
              </Gated>
            ) : null}
            {transitions.includes('rejected') ? (
              <Gated permission="sales.edit" reasonKey="perm.cannot.moveQuotes">
                <Button variant="outline" onClick={() => void act(() => setStatus.mutateAsync({ status: 'rejected' }))} disabled={busy}>
                  <X aria-hidden /> {t('quotes.actReject')}
                </Button>
              </Gated>
            ) : null}
            {canRevise(quote.status) ? (
              <Gated permission="sales.edit" reasonKey="perm.cannot.reviseQuotes">
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    void act(async () => {
                      await newRevision.mutateAsync();
                      void navigate(`/sales/quotes/${quote.id}`);
                    })
                  }
                >
                  <Copy aria-hidden /> {t('quotes.newRevision')}
                </Button>
              </Gated>
            ) : null}
            {canConvert(quote.status) && !quote.convertedOrderId && !quote.convertedInvoiceId ? (
              <Gated permission="sales.create" reasonKey="perm.cannot.convertQuotes">
                <Button variant="outline" onClick={convertToOrderNow} disabled={busy}>
                  <ArrowRightLeft aria-hidden /> {t('quotes.convertOrder')}
                </Button>
              </Gated>
            ) : null}
            {canConvert(quote.status) && !quote.convertedOrderId && !quote.convertedInvoiceId ? (
              <Gated permission="sales.create" reasonKey="perm.cannot.convertQuotes">
                <Button variant="outline" onClick={convertToInvoiceNow} disabled={busy}>
                  <ArrowRightLeft aria-hidden /> {t('quotes.convertInvoice')}
                </Button>
              </Gated>
            ) : null}
            <DocumentPdfButton request={pdfRequest(quote)} />
          </>
        );
      },
      lines: {
        columns: [
          { key: 'item', labelKey: 'quotes.colItem' },
          { key: 'qty', labelKey: 'sales.colQty', align: 'end', className: 'w-24' },
          { key: 'unitPrice', labelKey: 'sales.colUnitPrice', align: 'end', className: 'w-32' },
          { key: 'total', labelKey: 'sales.colTotal', align: 'end', className: 'w-32' },
        ],
        rows: (quote) =>
          quote.lines.map((line) => ({
            key: String(line.lineNo),
            cells: [
              <span key="d" className="font-medium">{line.description}</span>,
              <bdi key="q" dir="ltr" className="font-mono">{line.qty.replace(/\.?0+$/, '')}</bdi>,
              <Amount key="u" value={line.unitPrice} currency={quote.currency} />,
              <Amount key="t" value={line.lineTotal} currency={quote.currency} />,
            ],
          })),
      },
      totals: (quote) => ({
        labelKey: 'orders.netTaxTotal',
        amounts: [
          { value: quote.subtotal, currency: quote.currency },
          { value: quote.taxAmount, currency: quote.currency },
          { value: quote.grandTotal, currency: quote.currency, emphasis: true },
        ],
      }),
    },
  };

  return <DocumentListPage config={config} />;
}
